import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-medium transition-all duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] rounded-button cursor-pointer",
  {
    variants: {
      variant: {
        primary: "bg-brand-500 text-white hover:bg-brand-600 hover:scale-[1.02] shadow-sm",
        secondary: "border border-border-default bg-surface text-text-primary hover:bg-surface-secondary hover:scale-[1.02]",
        ghost: "text-text-secondary hover:bg-surface-tertiary hover:text-text-primary",
        danger: "bg-error-500 text-white hover:bg-error-600 hover:scale-[1.02]",
        volunteer: "bg-rose-500 text-white hover:bg-rose-600 hover:scale-[1.02] shadow-sm shadow-rose-500/25",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
